/* Saved views: the filters and columns somebody set up, kept.
 *
 * Stored per browser, deliberately. A view that follows a person between
 * devices is a server-side preference and belongs with the rest of them
 * (ERP-48); a view that survives a reload is what makes a list somebody's
 * own, and that is worth having now. Every read and write is wrapped:
 * localStorage throws in a private window and returns nothing after site
 * data is cleared, and a saved view is not worth failing a page load over.
 */
export type SavedView = {
  name: string
  filters: unknown[]
  columns?: string[]
  orderBy?: string
  search?: string
}

const KEY = (doctype: string) => `erp.views.${doctype}`

export function readViews(doctype: string): SavedView[] {
  try {
    const raw = localStorage.getItem(KEY(doctype))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? (parsed as SavedView[]) : []
  } catch {
    return []
  }
}

export function saveView(doctype: string, view: SavedView): SavedView[] {
  const next = [...readViews(doctype).filter((v) => v.name !== view.name), view]
  try { localStorage.setItem(KEY(doctype), JSON.stringify(next.slice(-12))) } catch { /* nothing depends on it sticking */ }
  return next
}

export function removeView(doctype: string, name: string): SavedView[] {
  const next = readViews(doctype).filter((v) => v.name !== name)
  try { localStorage.setItem(KEY(doctype), JSON.stringify(next)) } catch { /* as above */ }
  return next
}

/** The columns somebody chose for this list, if they chose any. */
export function readColumns(doctype: string): string[] | null {
  try {
    const raw = localStorage.getItem(`erp.columns.${doctype}`)
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? (parsed as string[]) : null
  } catch {
    return null
  }
}

export function writeColumns(doctype: string, columns: string[] | null) {
  try {
    if (columns) localStorage.setItem(`erp.columns.${doctype}`, JSON.stringify(columns))
    else localStorage.removeItem(`erp.columns.${doctype}`)
  } catch { /* as above */ }
}
